import { BrandLogo } from '@affine/component';
import { Button } from '@affine/component/ui/button';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { appNames } from '@affine/core/utils/channel';
import { Trans, useI18n } from '@affine/i18n';
import { LocalWorkspaceIcon } from '@blocksuite/icons/rc';
import { useServiceOptional } from '@toeverything/infra';
import type { MouseEvent } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { getOpenUrlInDesktopAppLink, openDesktopDownload } from '../utils';
import * as styles from './open-in-app-page.css';

interface OpenAppProps {
  urlToOpen?: string | null;
  openHereClicked?: (e: MouseEvent) => void;
  mode?: 'auth' | 'open-doc'; // default to 'auth'
}
const channel = BUILD_CONFIG.appBuildType;

export const OpenInAppPage = ({
  urlToOpen,
  openHereClicked,
  mode = 'auth',
}: OpenAppProps) => {
  // default to open the current page in desktop app
  urlToOpen ??= getOpenUrlInDesktopAppLink(window.location.href, true);
  const workspaceDialogService = useServiceOptional(WorkspaceDialogService);
  const openedUrlRef = useRef<string | null>(null);
  const t = useI18n();

  const openDownloadLink = useCallback(() => {
    openDesktopDownload().catch(console.error);
  }, []);

  const appName = appNames[channel];

  const goToAppearanceSetting = useCallback(
    (e: MouseEvent) => {
      openHereClicked?.(e);
      workspaceDialogService?.open('setting', {
        activeTab: 'appearance',
      });
    },
    [workspaceDialogService, openHereClicked]
  );

  useEffect(() => {
    if (!urlToOpen) {
      return;
    }

    if (openedUrlRef.current !== urlToOpen) {
      openedUrlRef.current = urlToOpen;
      location.href = urlToOpen;
    }
  }, [urlToOpen]);

  if (!urlToOpen) {
    return null;
  }

  return (
    <div className={styles.root}>
      <div className={styles.topNav}>
        <a href="/" rel="noreferrer" className={styles.affineLogo}>
          <BrandLogo size={24} />
        </a>

        <div className={styles.topNavLinks}>
          <a
            href="https://streamlain.de"
            target="_blank"
            rel="noreferrer"
            className={styles.topNavLink}
          >
            Website
          </a>
        </div>

        <Button onClick={openDownloadLink}>
          {t['com.streamlain.auth.open.streamlain.download-app']()}
        </Button>
      </div>

      <div className={styles.centerContent}>
        <BrandLogo size={120} aria-label={appName} />

        <div className={styles.prompt}>
          {mode === 'open-doc' ? (
            <Trans i18nKey="com.streamlain.auth.open.streamlain.open-doc-prompt">
              This doc is now opened in {appName}
            </Trans>
          ) : (
            <Trans i18nKey="com.streamlain.auth.open.streamlain.prompt">
              Open {appName} app now
            </Trans>
          )}
        </div>

        <div className={styles.promptLinks}>
          {openHereClicked && (
            <a
              className={styles.promptLink}
              onClick={openHereClicked}
              target="_blank"
              rel="noreferrer"
            >
              {t['com.streamlain.auth.open.streamlain.doc.open-here']()}
            </a>
          )}
          <a
            className={styles.promptLink}
            href={urlToOpen}
            target="_blank"
            rel="noreferrer"
          >
            {t['com.streamlain.auth.open.streamlain.try-again']()}
          </a>
        </div>
      </div>

      {mode === 'open-doc' ? (
        <div className={styles.docFooter}>
          <button
            className={styles.editSettingsLink}
            onClick={goToAppearanceSetting}
          >
            {t['com.streamlain.auth.open.streamlain.doc.edit-settings']()}
          </button>

          <div className={styles.docFooterText}>
            <LocalWorkspaceIcon width={16} height={16} />
            {t['com.streamlain.auth.open.streamlain.doc.footer-text']()}
          </div>
        </div>
      ) : null}
    </div>
  );
};
